import axios from "axios";
import { getCookieValue } from "./helpers";
import {
  BASE_URL,
  COUNTRY_CODE,
  WHITELABEL_EDGE_ID,
  WHITELABEL_EDGE_TOKEN,
} from "./constants";
import { WhitelabelConfig, Edgeconfig } from "@types";
import { Revalidator, RevalidatorOptions } from "swr";

export async function fetcher(url: string) {
  const websiteUid = getCookieValue("itk-website-uid");
  const locale = getCookieValue("NEXT_LOCALE");
  const instance = axios.create({
    baseURL: `${BASE_URL}/legacy/${COUNTRY_CODE}/shop/`,
    headers: {
      "content-language": locale,
      "itk-website-uid": websiteUid,
    },
  });

  return instance
    .get(url)
    .then((res) => res.data)
    .catch((err) => {
      throw err;
    });
}

export async function authorizedFetcher([url, token]: [string, string]) {
  const websiteUid = getCookieValue("itk-website-uid");
  const locale = getCookieValue("NEXT_LOCALE");
  const instance = axios.create({
    baseURL: `${BASE_URL}/legacy/${COUNTRY_CODE}/shop/`,
    headers: {
      Authorization: `Bearer ${token}`,
      "content-language": locale,
      "itk-website-uid": websiteUid,
    },
  });

  return instance
    .get(url)
    .then((res) => res.data)
    .catch((err) => {
      throw err;
    });
}

export const getEdgeItem = async <T = Edgeconfig>(
  key: string,
  revalidate?: number
): Promise<T> =>
  await fetch(
    `https://edge-config.vercel.com/${process.env.EDGE_ID}/item/${key}?token=${process.env.EDGE_TOKEN}`,
    // Use a cacheable fetch (instead of the @vercel/edge-config SDK's `get()`,
    // which reads with `revalidate: 0`). A `revalidate: 0` read forces every
    // route that calls it to bail out of static rendering, throwing a
    // DynamicServerError when Next tries to prerender pages such as /cart.
    // The banner config changes infrequently, so a short revalidate window is
    // plenty and keeps these routes statically renderable.
    { next: { revalidate: revalidate ?? 60 } }
  )
    .then((res) => res.json())
    .catch((err) => {
      throw err;
    });

export const getWLEdgeItem = async <T = WhitelabelConfig>(
  key: string
): Promise<T> =>
  await fetch(
    `https://edge-config.vercel.com/${WHITELABEL_EDGE_ID}/item/${key.replaceAll(
      ".",
      "-"
    )}?token=${WHITELABEL_EDGE_TOKEN}`
  )
    .then((res) => res.json())
    .catch((err) => {
      throw err;
    });

export const unPendOrder = (basketKey: string | undefined) =>
  axios.patch(
    `${BASE_URL}/legacy/${COUNTRY_CODE}/shop/basket/${basketKey}/checkout/unpend`
  );

export async function basketSummaryFetcher(url: string) {
  const websiteUid = getCookieValue("itk-website-uid");
  const locale = getCookieValue("NEXT_LOCALE");
  return axios
    .get(`${BASE_URL}/legacy/${COUNTRY_CODE}/shop/${url}`, {
      headers: {
        "content-language": locale,
        "itk-website-uid": websiteUid,
      },
    })
    .then(async (res) => {
      const { data } = res;

      if (!data) {
        await axios.delete("/api/basket");
        return null;
      }

      if (data.Ttl <= 0) {
        const basketKey = getCookieValue("ASP.NET_SessionId");
        const res = await fetch(
          `${BASE_URL}/legacy/${COUNTRY_CODE}/shop/basket/${basketKey}`,
          {
            method: "DELETE",
            headers: {
              "itk-website-uid": websiteUid ?? "",
            },
          }
        );
        if (res.status === 200) {
          await axios.delete("/api/basket");
        }
        return null;
      }

      return data;
    })
    .catch((err) => {
      throw err;
    });
}

export const MAX_429_RETRIES = 4;

export function handle429s(
  error: any,
  _: string,
  __: any,
  revalidate: Revalidator,
  { retryCount }: Required<RevalidatorOptions>
) {
  if (error.response.status !== 429) return;

  // Stop retrying after MAX_429_RETRIES attempts
  if (retryCount >= MAX_429_RETRIES) return;

  let delay = 5; // default 5 seconds

  // Try retry-after header first (in seconds)
  if (error.response.headers["retry-after"]) {
    const parsed = parseInt(error.response.headers["retry-after"], 10);
    if (!isNaN(parsed)) {
      delay = Math.max(parsed, 1);
    }
  }
  // Fall back to rl-twm header (in minutes)
  else if (error.response.headers["rl-twm"]) {
    const parsed = parseFloat(error.response.headers["rl-twm"]);
    if (!isNaN(parsed)) {
      delay = Math.round(parsed * 60);
    }
  }

  setTimeout(() => revalidate({ retryCount }), delay * 1000);
}
